/**
 * Tile Asset Registry
 *
 * Static catalog of all available PixelLab-generated tile assets for the map editor.
 * Three categories: base tiles (top-down), overlays (transparent features), edge tiles (isometric cliffs).
 *
 * Scale/offsetY values are proven from the HexMapMockup SVG prototype.
 */

// =====================================================================
// Types
// =====================================================================

export type BaseTileId =
  | 'grass-lush' | 'grass-dry' | 'dirt-road' | 'cobblestone'
  | 'water-shallow' | 'water-deep' | 'sand-desert' | 'swamp-mud'
  | 'snow' | 'lava-surface' | 'stone-ruins' | 'crystal-floor'
  | 'healing-glow';

export type EdgeTileId = 'cliff-grass' | 'cliff-snow' | 'cliff-desert' | 'cliff-lava';

export type OverlayTileId =
  | 'pine-trees' | 'oak-trees' | 'dead-trees' | 'mountain-peak'
  | 'crystal-cluster' | 'small-house' | 'ruins-pillar' | 'campfire'
  | 'wooden-bridge' | 'treasure-chest' | 'stone-altar' | 'watchtower';

export interface BaseTileDef {
  id: BaseTileId;
  label: string;
  path: string;
  edgeBiome: EdgeTileId;
}

export interface OverlayTileDef {
  id: OverlayTileId;
  label: string;
  path: string;
  scale: number;
  offsetY: number;
}

export interface EdgeTileDef {
  id: EdgeTileId;
  path: string;
}

// =====================================================================
// Base Tiles (top-down, fill entire hex)
// =====================================================================

export const BASE_TILES: Record<BaseTileId, BaseTileDef> = {
  'grass-lush':   { id: 'grass-lush',   label: 'Grass Plains',    path: '/mockup-tiles/td-grass-lush.png',   edgeBiome: 'cliff-grass' },
  'grass-dry':    { id: 'grass-dry',    label: 'Dry Grassland',   path: '/mockup-tiles/td-grass-dry.png',    edgeBiome: 'cliff-grass' },
  'dirt-road':    { id: 'dirt-road',    label: 'Dirt Path',       path: '/mockup-tiles/td-dirt-road.png',    edgeBiome: 'cliff-grass' },
  'cobblestone':  { id: 'cobblestone',  label: 'Cobblestone',     path: '/mockup-tiles/td-cobblestone.png',  edgeBiome: 'cliff-grass' },
  'water-shallow':{ id: 'water-shallow',label: 'Shallow Water',   path: '/mockup-tiles/td-water-shallow.png',edgeBiome: 'cliff-grass' },
  'water-deep':   { id: 'water-deep',   label: 'Deep Water',      path: '/mockup-tiles/td-water-deep.png',   edgeBiome: 'cliff-grass' },
  'sand-desert':  { id: 'sand-desert',  label: 'Desert Sand',     path: '/mockup-tiles/td-sand-desert.png',  edgeBiome: 'cliff-desert' },
  'swamp-mud':    { id: 'swamp-mud',    label: 'Murky Swamp',     path: '/mockup-tiles/td-swamp-mud.png',    edgeBiome: 'cliff-grass' },
  'snow':         { id: 'snow',         label: 'Snow Field',      path: '/mockup-tiles/td-snow.png',         edgeBiome: 'cliff-snow' },
  'lava-surface': { id: 'lava-surface', label: 'Lava Field',      path: '/mockup-tiles/td-lava-surface.png', edgeBiome: 'cliff-lava' },
  'stone-ruins':  { id: 'stone-ruins',  label: 'Stone Ruins',     path: '/mockup-tiles/td-stone-ruins.png',  edgeBiome: 'cliff-grass' },
  'crystal-floor':{ id: 'crystal-floor',label: 'Crystal Floor',   path: '/mockup-tiles/td-crystal-floor.png',edgeBiome: 'cliff-grass' },
  'healing-glow': { id: 'healing-glow', label: 'Healing Ground',   path: '/mockup-tiles/td-healing-glow.png', edgeBiome: 'cliff-grass' },
};

// =====================================================================
// Overlay Tiles (transparent background features)
// =====================================================================

export const OVERLAY_TILES: Record<OverlayTileId, OverlayTileDef> = {
  'pine-trees':     { id: 'pine-trees',     label: 'Pine Trees',     path: '/mockup-tiles/overlay-pine-trees.png',     scale: 1.5, offsetY: -18 },
  'oak-trees':      { id: 'oak-trees',      label: 'Oak Trees',      path: '/mockup-tiles/overlay-oak-trees.png',      scale: 1.4, offsetY: -14 },
  'dead-trees':     { id: 'dead-trees',     label: 'Dead Trees',     path: '/mockup-tiles/overlay-dead-trees.png',     scale: 0.9, offsetY: -6 },
  'mountain-peak':  { id: 'mountain-peak',  label: 'Mountain',       path: '/mockup-tiles/overlay-mountain-peak.png',  scale: 1.0, offsetY: -8 },
  'crystal-cluster':{ id: 'crystal-cluster',label: 'Crystal Cluster', path: '/mockup-tiles/overlay-crystal-cluster.png',scale: 0.9, offsetY: -6 },
  'small-house':    { id: 'small-house',    label: 'House',          path: '/mockup-tiles/overlay-small-house.png',    scale: 1.2, offsetY: -12 },
  'ruins-pillar':   { id: 'ruins-pillar',   label: 'Ruins Pillar',   path: '/mockup-tiles/overlay-ruins-pillar.png',   scale: 0.9, offsetY: -6 },
  'campfire':       { id: 'campfire',       label: 'Campfire',       path: '/mockup-tiles/overlay-campfire.png',       scale: 0.8, offsetY: -4 },
  'wooden-bridge':  { id: 'wooden-bridge',  label: 'Wooden Bridge',  path: '/mockup-tiles/overlay-wooden-bridge.png',  scale: 1.0, offsetY: -4 },
  'treasure-chest': { id: 'treasure-chest', label: 'Treasure Chest', path: '/mockup-tiles/overlay-treasure-chest.png', scale: 0.7, offsetY: -2 },
  'stone-altar':    { id: 'stone-altar',    label: 'Stone Altar',    path: '/mockup-tiles/overlay-stone-altar.png',    scale: 0.8, offsetY: -4 },
  'watchtower':     { id: 'watchtower',     label: 'Watchtower',     path: '/mockup-tiles/overlay-watchtower.png',     scale: 1.4, offsetY: -20 },
};

// =====================================================================
// Edge Tiles (isometric cliff tiles for perimeter hexes)
// =====================================================================

export const EDGE_TILES: Record<EdgeTileId, EdgeTileDef> = {
  'cliff-grass':  { id: 'cliff-grass',  path: '/mockup-tiles/iso-edge-cliff-grass.png' },
  'cliff-snow':   { id: 'cliff-snow',   path: '/mockup-tiles/iso-edge-cliff-snow.png' },
  'cliff-desert': { id: 'cliff-desert', path: '/mockup-tiles/iso-edge-cliff-desert.png' },
  'cliff-lava':   { id: 'cliff-lava',   path: '/mockup-tiles/iso-edge-cliff-lava.png' },
};

// =====================================================================
// Utility: Collect all tile paths for preloading
// =====================================================================

export function getAllTilePaths(): string[] {
  const paths: string[] = [];
  for (const tile of Object.values(BASE_TILES)) paths.push(tile.path);
  for (const tile of Object.values(OVERLAY_TILES)) paths.push(tile.path);
  for (const tile of Object.values(EDGE_TILES)) paths.push(tile.path);
  return paths;
}
